import { useState, useEffect } from "react";
import { PageLayout } from "@/components/PageLayout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Smartphone, Wifi, Calculator, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Install() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      toast.success("CalcLab installed successfully!");
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info("Installation is not available on this device/browser");
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      toast.success("Installation started!");
    }

    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const benefits = [
    {
      icon: Smartphone,
      title: "Answers in Your Pocket",
      description: "Access 100+ calculators instantly from your home screen, just like a native app"
    },
    {
      icon: Wifi,
      title: "Works Offline",
      description: "All calculators work without internet. Perfect for exams, labs, or anywhere without connection"
    },
    {
      icon: Calculator,
      title: "Lightning Fast",
      description: "No browser tabs needed. Launch instantly and solve problems in seconds"
    },
    {
      icon: Sparkles,
      title: "Always Updated",
      description: "Automatically receives new features and calculators without manual updates"
    }
  ];

  return (
    <PageLayout>
      <SEO
        title="Install CalcLab App - Free Offline Calculator App"
        description="Install CalcLab on your device for instant access to 100+ free science and math calculators. Works offline with no internet required."
        keywords="install calclab, calculator app, offline calculator, pwa calculator, mobile calculator app"
      />

      <div className="container max-w-6xl py-12 space-y-12">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-primary to-primary/60 shadow-lg mb-4">
            <Download className="w-12 h-12 text-primary-foreground" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">
            Get CalcLab on Your Device
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Install CalcLab for instant access to powerful calculators, even without internet. 
            Your complete science and math toolkit, always in your pocket.
          </p>
        </div>

        {/* Install Button */}
        {!isInstalled ? (
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Install CalcLab App
              </CardTitle>
              <CardDescription>
                {isInstallable 
                  ? "Click the button below to install CalcLab on your device"
                  : "Follow the instructions below to install CalcLab"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isInstallable && (
                <Button 
                  size="lg" 
                  className="w-full md:w-auto"
                  onClick={handleInstallClick}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install Now
                </Button>
              )}
              
              {!isInstallable && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h3 className="font-semibold mb-2">Installation Instructions:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="font-bold">iPhone/iPad:</span>
                        <span>Tap the Share button (square with arrow), then "Add to Home Screen"</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">Android:</span>
                        <span>Tap the menu (three dots), then "Install app" or "Add to Home Screen"</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">Desktop:</span>
                        <span>Look for the install icon in your browser's address bar</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">CalcLab is Installed!</h3>
              <p className="text-muted-foreground">
                You can now access CalcLab from your home screen or app drawer.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Benefits Grid */}
        <div>
          <h2 className="text-3xl font-bold text-center mb-8">
            Why Install CalcLab?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>{benefit.title}</CardTitle>
                  <CardDescription className="text-base">
                    {benefit.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Features List */}
        <Card>
          <CardHeader>
            <CardTitle>What You Get</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>100+ completely free calculators across physics, chemistry, math, and finance</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Full offline functionality - works without internet connection</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Step-by-step solutions and LaTeX export for all calculations</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Fast loading and instant access from your home screen</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>Automatic updates with new features and calculators</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>No app store required - install directly from your browser</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
